/**
 * A turf can be pulled from the site from the admin panel in two ways:
 * `deletedTurf` (listings show it as "Under Maintenance" and make the card
 * unclickable) and `hideTurf` (dropped from listings entirely). Listings honour
 * both, so the detail and booking routes have to as well - otherwise a direct
 * URL still reaches the enquiry form.
 */
export const isTurfPubliclyVisible = (turf: any): boolean => {
  if (!turf) return false;
  return turf.deletedTurf !== true && turf.hideTurf !== true;
};

/**
 * `hidePitch` was added after many pitch records already existed. Only an
 * explicit `true` hides a pitch so older API responses remain visible.
 */
export const isPitchPubliclyVisible = (pitch: any): boolean =>
  Boolean(pitch) && pitch.hidePitch !== true;

export const getVisiblePitches = <T>(pitches: T[] | null | undefined): T[] =>
  Array.isArray(pitches)
    ? pitches.filter((pitch) => isPitchPubliclyVisible(pitch))
    : [];

const getPitchNumber = (pitch: unknown): number | null => {
  const pitchName =
    typeof pitch === "object" &&
    pitch !== null &&
    "pitchName" in pitch &&
    typeof (pitch as { pitchName?: unknown }).pitchName === "string"
      ? (pitch as { pitchName: string }).pitchName
      : "";

  const match = pitchName.match(/(?:^|[-_])p0*(\d+)(?:[-_]|$)/i);
  return match ? Number(match[1]) : null;
};

/**
 * Prefer the lowest numbered visible pitch (normally p1) so the public URL
 * remains stable even when the API returns Pitch records in a different order.
 * Fall back to the first visible pitch for old names without a pitch number.
 */
export const getVisiblePitchesInCanonicalOrder = <T>(
  pitches: T[] | null | undefined
): T[] => {
  const visiblePitches = getVisiblePitches(pitches);
  let primaryIndex = -1;
  let lowestPitchNumber = Number.POSITIVE_INFINITY;

  visiblePitches.forEach((pitch, index) => {
    const pitchNumber = getPitchNumber(pitch);
    if (pitchNumber !== null && pitchNumber < lowestPitchNumber) {
      lowestPitchNumber = pitchNumber;
      primaryIndex = index;
    }
  });

  if (primaryIndex <= 0) return visiblePitches;

  return [
    visiblePitches[primaryIndex],
    ...visiblePitches.slice(0, primaryIndex),
    ...visiblePitches.slice(primaryIndex + 1),
  ];
};

export const getPrimaryVisiblePitch = <T>(
  pitches: T[] | null | undefined
): T | undefined => getVisiblePitchesInCanonicalOrder(pitches)[0];

export const hasVisiblePitches = (turf: any): boolean =>
  getVisiblePitches(turf?.Pitch).length > 0;

export const isBookablePublicTurf = (turf: any): boolean =>
  isTurfPubliclyVisible(turf) && hasVisiblePitches(turf);
